import { Link } from 'react-router-dom';

import { Button, Icon, InformationBox, Modal } from '@pukka/ui-components';
import { useSnapshot } from 'valtio';

import { BillingPersistedState, BillingState } from '../Billing.state';
import { BillingPriceItem, BillingPriceSummaryDetail, BillingPriceSummaryDiscount } from '../BillingReceipt';

interface BillingPriceBreakdownModalProps {
  readonly open?: boolean;
  readonly onToggleModal?: (open: boolean) => void;
}

function BillingPriceBreakdownModal({ open, onToggleModal }: BillingPriceBreakdownModalProps) {
  const { recurring } = useSnapshot(BillingPersistedState);
  const {
    employeeSlotCount,
    outletCount,
    plan,
    discountBanner,
    prices: { coupon, employeeSlots, outlets, total },
  } = recurring || { prices: {} };
  const { isRecurringFetching } = useSnapshot(BillingState);

  return (
    <Modal size="lg" unwrap open={open} onOpenChange={onToggleModal}>
      <Modal.Content className="bg-neutral-50">
        <Modal.Title className="flex-row justify-between">
          Price Breakdown{' '}
          <Modal.Trigger asChild>
            <Button variant="nude" size="none" className="rounded-md">
              <Icon icon="close" color="white" />
            </Button>
          </Modal.Trigger>
        </Modal.Title>
        <Modal.Body className="max-h-128">
          <div className="flex flex-col gap-2.5">
            {discountBanner ? (
              <InformationBox size="md" variant="success" icon>
                You get {discountBanner}
              </InformationBox>
            ) : null}
            <div className="rounded-xl bg-white">
              {employeeSlotCount ? (
                <BillingPriceItem
                  title="Staff Slots"
                  subTitle={`${employeeSlotCount} Slots`}
                  originalPrice={employeeSlots?.originalPrice}
                  finalPrice={employeeSlots?.finalPrice}
                  priceSuffix={employeeSlots?.unit}
                  loading={isRecurringFetching}
                  className="grid-cols-2"
                />
              ) : null}
              {outletCount ? (
                <BillingPriceItem
                  title={`Outlets (${outletCount})`}
                  subTitle={plan}
                  originalPrice={outlets?.originalPrice}
                  finalPrice={outlets?.finalPrice}
                  priceSuffix={outlets?.unit}
                  href={
                    <Modal.Close asChild>
                      <Link to="/v2/settings/billing/recurring/outlet" className="underline">{`Outlets (${outletCount})`}</Link>
                    </Modal.Close>
                  }
                  loading={isRecurringFetching}
                  className="grid-cols-2"
                />
              ) : null}
            </div>
            <div className="rounded-xl bg-white px-8 py-2">
              {coupon?.amount ? (
                <>
                  <BillingPriceSummaryDetail
                    loading={isRecurringFetching}
                    label="Sub Total"
                    price={(total?.finalPrice ?? 0) + coupon.amount}
                  >
                    {total?.finalPrice !== total?.originalPrice ? (
                      <BillingPriceSummaryDiscount
                        loading={isRecurringFetching}
                        price={total?.originalPrice}
                        description={total?.description}
                      />
                    ) : null}
                  </BillingPriceSummaryDetail>
                  <BillingPriceSummaryDetail loading={isRecurringFetching} label="Coupon Discount" price={-coupon.amount}>
                    <BillingPriceSummaryDiscount loading={isRecurringFetching} description={coupon.description} />
                  </BillingPriceSummaryDetail>
                </>
              ) : null}
              <BillingPriceSummaryDetail loading={isRecurringFetching} bigLabel label="Total" price={total?.finalPrice ?? 0}>
                {total?.finalPrice !== total?.originalPrice && !coupon?.amount ? (
                  <BillingPriceSummaryDiscount
                    loading={isRecurringFetching}
                    price={total?.originalPrice}
                    description={total?.description}
                  />
                ) : null}
              </BillingPriceSummaryDetail>
            </div>
          </div>
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
}

export { BillingPriceBreakdownModal };
